import { FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Button } from '@/components/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/shadcn/command'
import clsx from 'clsx'
import { BaseFieldProps } from './types'
import { useMemo, useState, useEffect } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { cn } from '@/lib/utils'
import { IconCheckMarkOutline } from '@icons/IconCheckMarkOutline'
import { IconDownChevronOutline } from '@icons/IconDownChevronOutline'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { IconInformationOutline } from '@icons/IconInformationOutline'
import { DocumentationButton } from './DocumentationButton'

interface SingleSelectComboboxFieldProps extends BaseFieldProps {
  applyButton?: React.ReactNode
  showIcons?: boolean
  popoverContentClassName?: string
}

export const SingleSelectComboboxField = ({
  field,
  formFieldName,
  control,
  variant,
  // applyButton,
  showIcons = false,
  popoverContentClassName,
}: SingleSelectComboboxFieldProps) => {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Memoize the filtered options to prevent recalculation on every render
  const filteredOptions = useMemo(() => {
    if (!field.choices?.length) return []

    // Only filter if there's a search query
    if (!searchQuery.trim()) return field.choices

    const lowerQuery = searchQuery.toLowerCase()
    return field.choices.filter((option) => {
      const label = typeof option === 'string' || typeof option === 'number' ? String(option) : option.title
      return label.toLowerCase().includes(lowerQuery)
    })
  }, [field.choices, searchQuery])

  // Reference for the parent container of virtualized items
  const [parentRef, setParentRef] = useState<HTMLDivElement | null>(null)

  // Setup virtualization with fixed item height to prevent overlapping
  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef,
    estimateSize: () => 36, // Fixed height for each item
    overscan: 5, // Number of items to render before/after the visible area
  })

  // Reset search query when popover closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('')
    }
  }, [open])

  return (
    <FormField
      control={control}
      name={formFieldName}
      render={({ field: formField }) => {
        const rawSelectedValue = formField.value as string | number | undefined | null
        const hasSelection = !(rawSelectedValue === '' || rawSelectedValue === undefined || rawSelectedValue === null)

        // Get the display label for the selected value
        const getSelectedLabel = () => {
          if (!hasSelection || !field?.choices?.length) return ''

          const selectedOption = field.choices.find((option) => {
            const value = typeof option === 'string' || typeof option === 'number' ? option : option.value
            return String(value) === String(rawSelectedValue)
          })

          if (!selectedOption) return ''
          return typeof selectedOption === 'string' || typeof selectedOption === 'number'
            ? String(selectedOption)
            : selectedOption.title
        }

        // Define the handler for single selection
        const handleSelect = (optionValue: string | number) => {
          // If the same value is selected, clear the selection
          if (String(rawSelectedValue) === String(optionValue)) {
            formField.onChange('')
          } else {
            // Otherwise set the new value
            formField.onChange(optionValue)
          }
          // Close the popover after selection
          setOpen(false)
        }

        const isCompact = variant === 'row' || variant === 'inline'
        const fieldControl = (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                // className={cn('flex w-full items-center justify-between gap-2 rounded-sm', {
                className={cn('flex w-full items-center justify-between gap-2', {
                  'h-6 min-h-0 min-w-48 max-w-48 py-0': variant === 'row',
                  'h-6 min-h-0 border-none py-0': variant === 'inline',
                  'h-8 py-0': variant !== 'row' && variant !== 'inline',
                })}
                variant="input"
                role="combobox"
                aria-expanded={open}
                disabled={field.readOnly}
              >
                <div className="max-w-80 flex-1 truncate text-left">
                  {hasSelection ? (
                    showIcons ? (
                      <div className="inline-flex items-center">
                        <DynamicIcon size={20} className="mr-3" iconName={String(formField.value)} lazyLoad={false} />
                        {getSelectedLabel()}
                      </div>
                    ) : (
                      getSelectedLabel()
                    )
                  ) : (
                    <span className="text-muted-foreground">{`Select ${field.title.toLowerCase()}`}</span>
                  )}
                </div>
                <IconDownChevronOutline className="size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className={cn(
                'w-[var(--radix-popover-trigger-width)] p-0',
                variant === 'inline' && 'min-w-80',
                popoverContentClassName
              )}
            >
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder={`Search ${field.title.toLowerCase()}...`}
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                <CommandList>
                  <CommandEmpty>No options found.</CommandEmpty>
                  <CommandGroup>
                    {/* Virtualized list container */}
                    <div
                      ref={setParentRef}
                      className="max-h-[200px] w-full overflow-auto"
                      style={{
                        height: Math.min(filteredOptions.length * 36, 200),
                        width: '100%',
                      }}
                    >
                      {/* Set the total height of the virtualizer */}
                      <div
                        style={{
                          height: `${rowVirtualizer.getTotalSize()}px`,
                          width: '100%',
                          position: 'relative',
                        }}
                      >
                        {/* Only render visible items */}
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                          const option = filteredOptions[virtualRow.index]
                          if (!option) return null // Safety check

                          const optionValue =
                            typeof option === 'string' || typeof option === 'number' ? option : option.value
                          const optionLabel =
                            typeof option === 'string' || typeof option === 'number' ? String(option) : option.title
                          const isSelected = String(rawSelectedValue) === String(optionValue)

                          return (
                            <Tooltip key={virtualRow.key}>
                              <TooltipTrigger asChild>
                                <div
                                  data-index={virtualRow.index}
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: `${virtualRow.size}px`,
                                    transform: `translateY(${virtualRow.start}px)`,
                                  }}
                                >
                                  <CommandItem
                                    value={String(optionValue)}
                                    onSelect={() => handleSelect(optionValue)}
                                    className="h-9"
                                  >
                                    {!showIcons && (
                                      <IconCheckMarkOutline
                                        className={clsx(
                                          'mr-4 size-4 shrink-0',
                                          isSelected ? 'opacity-100' : 'opacity-0'
                                        )}
                                      />
                                    )}
                                    <span className="truncate">
                                      {typeof option === 'string' && showIcons ? (
                                        <div className="flex items-center gap-x-2">
                                          <DynamicIcon size={20} iconName={option} />
                                          {optionLabel}
                                        </div>
                                      ) : (
                                        optionLabel
                                      )}
                                    </span>
                                  </CommandItem>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="bg-foreground text-xs text-offwhite">
                                <p>{optionLabel}</p>
                              </TooltipContent>
                            </Tooltip>
                          )
                        })}
                      </div>
                    </div>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )

        return (
          <FormItem
            className={clsx(
              'flex flex-1 flex-col',
              field.widget === 'hidden' && 'hidden',
              isCompact ? 'space-y-0' : ''
            )}
          >
            <div className={clsx('flex justify-start gap-2', { hidden: variant === 'inline' })}>
              <FormLabel className={variant === 'row' ? 'sr-only' : 'block'}>{field.title}</FormLabel>
              {!isCompact && <DocumentationButton documentation={field.documentation} />}
            </div>
            <FormDescription
              className={clsx({
                'sr-only': variant === 'row',
                hidden: variant === 'inline',
                'block text-xs': !isCompact,
              })}
            >
              {field.description}
            </FormDescription>
            {variant === 'row' ? (
              <div className="flex items-center gap-x-1 text-muted-foreground">
                <TooltipButton tooltip={field.title}>
                  <div className="shrink-0">
                    <IconInformationOutline className="size-4" />
                  </div>
                </TooltipButton>
                {fieldControl}
              </div>
            ) : (
              fieldControl
            )}
            {!isCompact && <FormMessage />}
          </FormItem>
        )
      }}
    />
  )
}
